import { useState } from 'react';

import { useActiveOrg } from '../active-org.ts';
import { useAuth, useTeams, type Team } from '../api.ts';

/** Key used for engineers on no team, in both the filter and groupings. */
export const UNASSIGNED_KEY = 'unassigned';
export const UNASSIGNED_LABEL = 'Unassigned';

/**
 * Per-view "filter by team", multi-select. Resolves the active org's teams,
 * tracks the selected team keys, and narrows an engineer list to that set.
 *
 * Each engineer is on at most one team: an explicit assignment, else the
 * special "unassigned" key. An empty selection means "All teams" (everyone,
 * including unassigned). The control hides itself when the org has no teams.
 */
export interface TeamFilter {
  selected: string[];
  setSelected: (value: string[]) => void;
  options: Array<{ value: string; label: string }>;
  /** Whether to render the control. */
  show: boolean;
  /** Narrow engineers to the selected teams (no-op for an empty selection). */
  filter: <E extends { id: string }>(engineers: E[]) => E[];
}

function memberTeamMap(teams: Team[]): Map<string, string> {
  const m = new Map<string, string>();
  for (const t of teams) for (const id of t.member_ids) m.set(id, String(t.id));
  return m;
}

export function useTeamFilter(): TeamFilter {
  const auth = useAuth();
  const orgs = auth.status === 'signed-in' ? auth.me.orgs : [];
  const { active } = useActiveOrg(orgs);
  const teams = useTeams(active?.slug ?? null).data ?? [];

  const [selected, setSelected] = useState<string[]>([]);

  const byEngineer = memberTeamMap(teams);
  const teamOf = (engId: string) => byEngineer.get(engId) ?? UNASSIGNED_KEY;

  const options = [
    ...teams.map((t) => ({ value: String(t.id), label: t.name })),
    { value: UNASSIGNED_KEY, label: UNASSIGNED_LABEL },
  ];

  return {
    selected,
    setSelected,
    options,
    show: teams.length > 0,
    filter: (engineers) =>
      selected.length === 0 ? engineers : engineers.filter((e) => selected.includes(teamOf(e.id))),
  };
}

/**
 * A pure engineer→team grouping for bucketing a per-engineer dataset by
 * team (e.g. the accounting Sankey). Engineers on no team land in an
 * "Unassigned" bucket. Keys are team ids as strings (or `UNASSIGNED_KEY`).
 */
export interface TeamGrouping {
  keyOf: (engineerId: string) => string;
  labelOf: (key: string) => string;
  order: string[];
}

export function buildTeamGrouping(teams: Team[]): TeamGrouping {
  const byEngineer = memberTeamMap(teams);
  const labelByKey = new Map<string, string>(teams.map((t) => [String(t.id), t.name]));

  return {
    keyOf: (engineerId) => byEngineer.get(engineerId) ?? UNASSIGNED_KEY,
    labelOf: (key) => (key === UNASSIGNED_KEY ? UNASSIGNED_LABEL : (labelByKey.get(key) ?? UNASSIGNED_LABEL)),
    order: [...teams.map((t) => String(t.id)), UNASSIGNED_KEY],
  };
}
